import { useEffect } from 'react';
import { request } from '../api/client';
import { useAuthStore } from '../store/auth';

/**
 * Keeps the logged-in user's role + permissions in sync with the server so
 * permission changes take effect WITHOUT forcing a full re-login.
 *
 * The JWT only carries the role code; the backend resolves the live permission
 * set per request. This hook mirrors that on the client by re-fetching
 * `GET /auth/me` and merging the result into the auth store:
 *   • once on mount (covers "logged in before the change"),
 *   • whenever the tab regains focus (covers "admin changed it in another tab"),
 *   • on a slow interval as a safety net.
 *
 * It only writes to the store when something actually changed, so it won't
 * cause needless re-renders on every tick.
 */
export function useAuthRefresh() {
  const token = useAuthStore((s) => s.accessToken);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const refresh = async () => {
      try {
        const me = await request<any>({ url: '/auth/me' });
        if (cancelled || !me) return;
        const store = useAuthStore.getState();
        const cur = store.user;
        if (!cur) return;

        const nextPerms: string[] = me.permissions ?? [];
        const curPerms = cur.permissions ?? [];
        const changed =
          cur.role !== me.role ||
          curPerms.length !== nextPerms.length ||
          nextPerms.some((p) => !curPerms.includes(p)) ||
          cur.profilePhotoUrl !== me.profilePhotoUrl ||
          cur.fullName !== me.fullName;

        if (changed) {
          store.syncUser({
            role: me.role, roles: me.roles ?? [me.role], permissions: nextPerms, territoryId: me.territoryId,
            profilePhotoUrl: me.profilePhotoUrl ?? null, fullName: me.fullName,
          });
        }
      } catch {
        // Transient error (offline / 401 handled elsewhere): keep last-known perms.
      }
    };

    refresh();
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    const id = window.setInterval(refresh, 60_000);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      window.clearInterval(id);
    };
  }, [token]);
}
